import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  IconButton,
  AppBar,
  Toolbar,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';
import TiptapEditor from './TiptapEditor';

const CrearPlantillaDialog = ({ open, onClose, onSave, plantilla, isEdit }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    contenido_html: '',
    tipo: 'contrato_prestamo',
    id_productofinanciero: '',
    activo: true
  });
  const [variables, setVariables] = useState([]);
  const [productosFinancieros, setProductosFinancieros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const [searchVariable, setSearchVariable] = useState('');
  const editorRef = useRef(null);

  const showAlert = (msg, type = 'success') => {
    setMessageAlert(msg);
    setAlertType(type);
    setOpenAlert(true);
  };

  const fetchVariables = async (idProductofinanciero) => {
    try {
      const url = new URL(`${process.env.REACT_APP_SERVER_URL}/api/plantillas/variables`);
      if (idProductofinanciero) {
        url.searchParams.set('id_productofinanciero', idProductofinanciero);
      }
      const response = await fetch(url.toString(), {
        headers: {
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      if (response.ok) {
        const data = await response.json();
        setVariables(data);
      }
    } catch (error) {
      console.error('Error fetching variables:', error);
    }
  };

  const fetchProductosFinancieros = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/prodfinancieros/${localStorage.getItem('idcompany')}`,
        {
          headers: {
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );
      if (!response.ok) return;
      const data = await response.json();
      const list = (Array.isArray(data) ? data : []).map((p) => {
        let nombre = '';
        try {
          const generales = typeof p.generales === 'string' ? JSON.parse(p.generales) : p.generales;
          nombre = generales?.nombre || '';
        } catch (_) {
          nombre = '';
        }
        return {
          id: p.idprodfinanciero || p.id,
          nombre: nombre || 'Producto sin nombre'
        };
      });
      // dedup por id (la query del backend hace LEFT JOIN con catinventario y puede duplicar)
      const seen = new Set();
      const dedup = list.filter((p) => {
        if (!p.id || seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
      setProductosFinancieros(dedup);
    } catch (error) {
      console.error('Error fetching productos financieros:', error);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchProductosFinancieros();
    const initialProducto = isEdit && plantilla ? plantilla.id_productofinanciero || '' : '';
    fetchVariables(initialProducto);
    if (isEdit && plantilla) {
      setFormData({
        nombre: plantilla.nombre || '',
        descripcion: plantilla.descripcion || '',
        contenido_html: plantilla.contenido_html || '',
        tipo: plantilla.tipo || 'contrato_prestamo',
        id_productofinanciero: plantilla.id_productofinanciero || '',
        activo: plantilla.activo !== undefined ? plantilla.activo : true
      });
      setTimeout(() => editorRef.current?.setContent(plantilla.contenido_html || ''), 50);
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        contenido_html: '',
        tipo: 'contrato_prestamo',
        id_productofinanciero: '',
        activo: true
      });
      setTimeout(() => editorRef.current?.setContent(''), 50);
    }
  }, [open, isEdit, plantilla]);

  // Refetch variables al cambiar el producto financiero seleccionado
  useEffect(() => {
    if (!open) return;
    fetchVariables(formData.id_productofinanciero);
  }, [formData.id_productofinanciero, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const filteredVariables = variables.filter(variable =>
    variable.label.toLowerCase().includes(searchVariable.toLowerCase()) ||
    variable.name.toLowerCase().includes(searchVariable.toLowerCase()) ||
    (variable.example && variable.example.toLowerCase().includes(searchVariable.toLowerCase()))
  );

  // Agrupamos las variables por categoría para que el panel sea más legible
  const groupedVariables = useMemo(() => {
    const groups = new Map();
    filteredVariables.forEach((v) => {
      const cat = v.category || 'Variables del préstamo';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(v);
    });
    return Array.from(groups.entries());
  }, [filteredVariables]);

  const insertVariable = (variable) => {
    editorRef.current?.insertVariable(variable.name, variable.label);
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      showAlert('El nombre es requerido', 'error');
      return;
    }
    const html = editorRef.current?.getHTML() || '';
    const plainText = html.replace(/<[^>]*>/g, '').trim();
    if (!plainText) {
      showAlert('El contenido es requerido', 'error');
      return;
    }

    try {
      setLoading(true);
      const url = isEdit
        ? `${process.env.REACT_APP_SERVER_URL}/api/plantillas/${plantilla.id}`
        : `${process.env.REACT_APP_SERVER_URL}/api/plantillas`;
      const method = isEdit ? 'PUT' : 'POST';

      const requestData = {
        ...formData,
        contenido_html: html,
        id_productofinanciero: formData.id_productofinanciero || null,
        idcompany: localStorage.getItem('idcompany'),
        created_by: localStorage.getItem('id')
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        onSave();
      } else {
        const error = await response.json();
        showAlert(error.error || 'Error al guardar la plantilla', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showAlert('Error al guardar la plantilla', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{ sx: { backgroundColor: '#f5f7f9' } }}
      >
        <AppBar position="relative" elevation={0} sx={{ backgroundColor: '#18586b' }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
              {isEdit ? 'Editar Plantilla' : 'Crear Nueva Plantilla'}
            </Typography>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              color="primarybutton"
              sx={{ fontWeight: 600, mr: 1 }}
            >
              {loading ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear')}
            </Button>
            <IconButton onClick={onClose} disabled={loading} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
            {/* Left Panel - Form + Editor */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Nombre de la Plantilla"
                  value={formData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  required
                  size="small"
                  sx={{ flex: 2, minWidth: 220 }}
                />
                <TextField
                  label="Tipo"
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                  select
                  SelectProps={{ native: true }}
                  size="small"
                  sx={{ flex: 1, minWidth: 160 }}
                >
                  <option value="contrato_prestamo">Contrato de Préstamo</option>
                </TextField>
                <TextField
                  label="Producto Financiero"
                  value={formData.id_productofinanciero || ''}
                  onChange={(e) => handleInputChange('id_productofinanciero', e.target.value)}
                  select
                  size="small"
                  helperText="Carga campos del formulario como variables"
                  sx={{ flex: 2, minWidth: 240 }}
                >
                  <MenuItem value="">
                    <em>Sin producto (solo variables base)</em>
                  </MenuItem>
                  {productosFinancieros.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField
                label="Descripción"
                value={formData.descripcion}
                onChange={(e) => handleInputChange('descripcion', e.target.value)}
                fullWidth
                size="small"
              />

              <TiptapEditor
                ref={editorRef}
                initialContent={formData.contenido_html}
                onChange={(html) => handleInputChange('contenido_html', html)}
                onAlert={showAlert}
              />
            </Box>

            {/* Right Panel - Variables */}
            <Box
              sx={{
                width: 320,
                backgroundColor: 'white',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Variables Disponibles
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                Haz clic en una variable para insertarla en el contenido
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Buscar variable..."
                value={searchVariable}
                onChange={(e) => setSearchVariable(e.target.value)}
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-track': { backgroundColor: '#f0f0f0', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#55b4ac',
                    borderRadius: '4px',
                    '&:hover': { backgroundColor: '#18586b' }
                  }
                }}
              >
                {filteredVariables.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No se encontraron variables
                  </Typography>
                ) : (
                  groupedVariables.map(([category, vars]) => (
                    <Box key={category} sx={{ mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontWeight: 700,
                          color: '#18586b',
                          mb: 0.5,
                          mt: 0.5
                        }}
                      >
                        {category}
                      </Typography>
                      {vars.map((variable, index) => (
                        <Card
                          key={`${category}-${variable.name}-${index}`}
                          sx={{ mb: 1, cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                          onClick={() => insertVariable(variable)}
                        >
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="medium" noWrap>
                                  {variable.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {variable.name}
                                </Typography>
                              </Box>
                              <Chip
                                label={`{{${variable.name}}}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ ml: 1, flexShrink: 0 }}
                              />
                            </Box>
                            {variable.example && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }} noWrap>
                                Ejemplo: {variable.example}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ))
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Instrucciones
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  • Selecciona un <strong>Producto Financiero</strong> para que sus campos del formulario aparezcan como variables.<br />
                  • Usa <strong>Cargar Word</strong> para importar un .docx existente.<br />
                  • Las variables {`{{...}}`} dentro del Word se detectan automáticamente.<br />
                  • Inserta variables haciendo clic en la lista.<br />
                  • Tamaño máximo de imagen: 2MB. Word: 10MB.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <AlertMessageDialog
        open={openAlert}
        handleClose={() => setOpenAlert(false)}
        message={messageAlert}
        severity={alertType}
        title=""
      />
    </>
  );
};

export default CrearPlantillaDialog;
